import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import it from './locales/it.json';
import en from './locales/en.json';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguages } from './types';

const LANGUAGE_STORAGE_KEY = 'fit-ui-language';

// Get language from localStorage or default to DEFAULT_LANGUAGE
const getStoredLanguage = (): SupportedLanguages => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguages)) {
      return stored as SupportedLanguages;
    }
  } catch (e) {
    // localStorage might not be available
  }
  return DEFAULT_LANGUAGE;
};

// Save language to localStorage
export const setStoredLanguage = (lng: string): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch (e) {
    // localStorage might not be available
  }
};

// Function to fetch user language from backend (to be called after authentication)
export const fetchUserLanguage = async (accountApi: {
  getAccountLanguage: () => Promise<{ language: string }>;
}): Promise<string | null> => {
  try {
    const response = await accountApi.getAccountLanguage();
    return response.language || null;
  } catch (e) {
    // If user is not authenticated or endpoint fails, return null
    return null;
  }
};

// Function to initialize i18n with user language if available
export const initializeI18nWithUserLanguage = async (userLanguage: string | null): Promise<void> => {
  if (userLanguage && SUPPORTED_LANGUAGES.includes(userLanguage as SupportedLanguages)) {
    await i18n.changeLanguage(userLanguage);
    setStoredLanguage(userLanguage);
  }
};

i18n.use(initReactI18next).init({
  resources: {
    it: {
      translation: it,
    },
    en: {
      translation: en,
    },
  },
  lng: getStoredLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false, // React already escapes values
  },
  react: {
    useSuspense: false, // Disable suspense for better compatibility
  },
});

// Listen for language changes and save to localStorage
i18n.on('languageChanged', (lng) => {
  setStoredLanguage(lng);
});

export default i18n;
